<script setup>
// A PRÉVIA: o modelo como chega no celular do cliente (25/09/2026).
//
// A referência é o "Preview em tempo real" do portal da WAME, com o que falta
// lá: aqui a variável já aparece com o EXEMPLO (Maria, 10/10 às 14h) e o
// *negrito* sai negrito — é assim que a Meta avalia, e é assim que o cliente
// lê. Variável sem exemplo fica marcada em âmbar, para a pessoa ver o que falta.
//
// As cores do topo e do papel de parede são as do WhatsApp de propósito: a
// prévia é um retrato do aplicativo do cliente, não uma peça do advup.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatarComoWhatsapp } from 'dashboard/helper/modeloWhatsapp';
import { iniciaisDoNome } from 'dashboard/helper/avatar';

const props = defineProps({
  modelo: { type: Object, required: true },
  // O número da caixa: nome verificado e telefone (vem da WAME).
  perfil: { type: Object, default: () => ({}) },
  // Endereço local da imagem escolhida para o cabeçalho (URL.createObjectURL).
  midiaUrl: { type: String, default: '' },
});

const { t } = useI18n();
const P = 'WHATSAPP_TEMPLATES.MODELOS.PREVIA';

const nome = computed(() => props.perfil.verifiedName || t(`${P}.ESCRITORIO`));
const iniciais = computed(() => iniciaisDoNome(nome.value));
const cab = computed(() => props.modelo.cabecalho);

// O texto é de quem digita: `formatarComoWhatsapp` escapa antes de marcar.
const cabecalhoHtml = computed(() =>
  formatarComoWhatsapp(cab.value.texto, [cab.value.exemplo])
);
const corpoHtml = computed(() =>
  props.modelo.corpo.trim()
    ? formatarComoWhatsapp(props.modelo.corpo, props.modelo.exemplos)
    : ''
);

const ICONE_DA_MIDIA = {
  IMAGE: 'i-ph-image-fill',
  VIDEO: 'i-ph-video-camera-fill',
  DOCUMENT: 'i-ph-file-pdf-fill',
};
const ICONE_DO_BOTAO = {
  QUICK_REPLY: 'i-ph-arrow-bend-up-left-bold',
  URL: 'i-ph-arrow-square-out-bold',
  PHONE_NUMBER: 'i-ph-phone-bold',
};

const hora = new Date().toLocaleTimeString('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
});
</script>

<template>
  <figure class="mdl-cel" :aria-label="t(`${P}.ROTULO`)">
    <div class="mdl-cel__tela">
      <header class="mdl-cel__topo">
        <i class="i-ph-caret-left-bold mdl-cel__volta" aria-hidden="true" />
        <span class="mdl-cel__rosto" aria-hidden="true">{{ iniciais }}</span>
        <span class="mdl-cel__quem">
          <strong class="mdl-cel__nome">{{ nome }}</strong>
          <span v-if="perfil.displayPhoneNumber" class="mdl-cel__fone">
            {{ perfil.displayPhoneNumber }}
          </span>
        </span>
      </header>

      <div class="mdl-cel__conversa">
        <span class="mdl-cel__dia">{{ t(`${P}.HOJE`) }}</span>

        <div class="mdl-cel__bolha">
          <!-- eslint-disable vue/no-v-html -->
          <p
            v-if="cab.tipo === 'TEXT' && cab.texto.trim()"
            class="mdl-cel__cab"
            v-html="cabecalhoHtml"
          />
          <div
            v-else-if="cab.tipo !== 'NONE' && cab.tipo !== 'TEXT'"
            class="mdl-cel__midia"
            :class="{
              'mdl-cel__midia--foto': cab.tipo === 'IMAGE' && midiaUrl,
            }"
          >
            <img
              v-if="cab.tipo === 'IMAGE' && midiaUrl"
              :src="midiaUrl"
              alt=""
              class="mdl-cel__img"
            />
            <i v-else :class="ICONE_DA_MIDIA[cab.tipo]" aria-hidden="true" />
          </div>
          <p v-if="corpoHtml" class="mdl-cel__corpo" v-html="corpoHtml" />
          <!-- eslint-enable vue/no-v-html -->
          <p v-else class="mdl-cel__corpo mdl-cel__corpo--vazio">
            {{ t(`${P}.CORPO_VAZIO`) }}
          </p>
          <p v-if="modelo.rodape.trim()" class="mdl-cel__rodape">
            {{ modelo.rodape }}
          </p>
          <span class="mdl-cel__hora">{{ hora }}</span>
        </div>

        <TransitionGroup
          v-if="modelo.botoes.length"
          tag="ul"
          name="mdl-cel-botao"
          class="mdl-cel__botoes"
        >
          <li
            v-for="(b, i) in modelo.botoes"
            :key="`${i}-${b.tipo}`"
            class="mdl-cel__botao"
          >
            <i :class="ICONE_DO_BOTAO[b.tipo]" aria-hidden="true" />
            <span>{{ b.texto.trim() || t(`${P}.BOTAO_VAZIO`) }}</span>
          </li>
        </TransitionGroup>
      </div>
    </div>
  </figure>
</template>

<style scoped>
/* O aparelho: moldura escura, cantos de celular. O raio é o dobro do maior
   da escala — telefone não tem canto de cartão.

   🔴 AS CORES SÃO DO WHATSAPP, FIXAS, E NÃO DA NOSSA ESCALA (25/09/2026).
   A escala do advup INVERTE no tema escuro (slate-12 vira quase branco): a
   primeira versão saiu com moldura branca e topo verde-claro no escuro. A
   prévia é um retrato do aplicativo do CLIENTE, então usa o tema claro do
   WhatsApp aqui e o escuro dele no nosso escuro — como o cliente veria. */
.mdl-cel {
  --cel-moldura: rgb(15 23 42);
  --cel-topo: rgb(0 128 105);
  --cel-papel: rgb(239 234 226);
  --cel-trama: rgb(17 27 33 / 6%);
  --cel-bolha: rgb(255 255 255);
  --cel-tinta: rgb(17 27 33);
  --cel-apoio: rgb(102 119 129);
  --cel-link: rgb(2 126 181);
  --cel-sombra: rgb(11 20 26 / 13%);

  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  padding: var(--esp-2);
  border-radius: calc(var(--r-5) * 2);
  background: var(--cel-moldura);
  box-shadow:
    0 1px 2px rgb(15 23 42 / 20%),
    0 24px 48px -24px rgb(15 23 42 / 45%);
}

.dark .mdl-cel {
  --cel-moldura: rgb(2 6 16);
  --cel-topo: rgb(31 44 52);
  --cel-papel: rgb(11 20 26);
  --cel-trama: rgb(233 237 239 / 4%);
  --cel-bolha: rgb(32 44 51);
  --cel-tinta: rgb(233 237 239);
  --cel-apoio: rgb(134 150 160);
  --cel-link: rgb(83 189 235);
  --cel-sombra: rgb(0 0 0 / 30%);
  box-shadow:
    0 0 0 1px rgb(255 255 255 / 8%),
    0 24px 48px -24px rgb(0 0 0 / 60%);
}

.mdl-cel__tela {
  display: flex;
  flex-direction: column;
  min-height: 460px;
  overflow: hidden;
  border-radius: calc(var(--r-5) * 1.5);
  background: var(--cel-papel);
}

.mdl-cel__topo {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-3) var(--esp-3) var(--esp-3) var(--esp-2);
  background: var(--cel-topo);
  color: rgb(255 255 255);
}

.mdl-cel__volta {
  font-size: var(--ico-2);
  opacity: 0.85;
}

.mdl-cel__rosto {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: var(--r-redondo);
  background: rgb(255 255 255 / 18%);
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  line-height: 1;
}

.mdl-cel__quem {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.mdl-cel__nome {
  overflow: hidden;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mdl-cel__fone {
  font-size: var(--txt-rotulo);
  line-height: var(--linha-normal);
  opacity: 0.85;
}

/* O papel de parede: a trama de pontos do WhatsApp, em fuga para baixo. */
.mdl-cel__conversa {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-1);
  padding: var(--esp-3);
  background-image: radial-gradient(var(--cel-trama) 1px, transparent 1px);
  background-size: 16px 16px;
}

.mdl-cel__dia {
  align-self: center;
  margin-bottom: var(--esp-2);
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-2);
  background: var(--cel-bolha);
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: var(--cel-apoio);
  box-shadow: 0 1px 1px var(--cel-sombra);
}

.mdl-cel__bolha {
  position: relative;
  align-self: flex-start;
  width: 92%;
  padding: var(--esp-1) var(--esp-2) var(--esp-4);
  border-radius: 0 var(--r-3) var(--r-3) var(--r-3);
  background: var(--cel-bolha);
  color: var(--cel-tinta);
  box-shadow: 0 1px 1px var(--cel-sombra);
  animation: mdl-cel-chega var(--mov-3) var(--mov-curva) backwards;
}

.mdl-cel__cab {
  margin: var(--esp-1) 0 0;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-rotulo);
  line-height: var(--linha-normal);
}

.mdl-cel__midia {
  display: grid;
  place-items: center;
  height: 120px;
  margin: 0 calc(var(--esp-1) * -1) var(--esp-1);
  overflow: hidden;
  border-radius: var(--r-2);
  background: var(--cel-trama);
  color: var(--cel-apoio);
  font-size: var(--ico-4);
}

.mdl-cel__midia--foto {
  height: auto;
  max-height: 180px;
}

.mdl-cel__img {
  display: block;
  width: 100%;
  object-fit: cover;
}

.mdl-cel__corpo {
  margin: var(--esp-1) 0 0;
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  overflow-wrap: anywhere;
}

.mdl-cel__corpo--vazio {
  color: var(--cel-apoio);
}

.mdl-cel__corpo :deep(code) {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: var(--txt-nota);
}

/* A variável com o exemplo aparece como o cliente lê — só um sublinhado
   pontilhado lembra que ali muda a cada envio. Sem exemplo, âmbar: falta. */
.mdl-cel__corpo :deep(.mdl-var),
.mdl-cel__cab :deep(.mdl-var) {
  text-decoration: underline dotted var(--cel-topo);
  text-underline-offset: 3px;
}

.mdl-cel__corpo :deep(.mdl-var--vazia),
.mdl-cel__cab :deep(.mdl-var--vazia) {
  padding: 0 var(--esp-1);
  border-radius: var(--r-1);
  background: rgb(var(--amber-3));
  color: rgb(var(--amber-12));
  text-decoration: none;
}

.mdl-cel__rodape {
  margin: var(--esp-1) 0 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  color: var(--cel-apoio);
}

.mdl-cel__hora {
  position: absolute;
  right: var(--esp-2);
  bottom: var(--esp-1);
  font-size: var(--txt-rotulo);
  line-height: var(--linha-apertada);
  color: var(--cel-apoio);
}

.mdl-cel__botoes {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  width: 92%;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mdl-cel__botao {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--esp-1);
  min-height: 36px;
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-3);
  background: var(--cel-bolha);
  font-size: var(--txt-corpo);
  font-weight: var(--peso-dado);
  line-height: var(--linha-apertada);
  color: var(--cel-link);
  box-shadow: 0 1px 1px var(--cel-sombra);
}

.mdl-cel__botao > i {
  flex-shrink: 0;
  font-size: var(--ico-1);
}

.mdl-cel-botao-enter-active,
.mdl-cel-botao-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva);
}

.mdl-cel-botao-enter-from,
.mdl-cel-botao-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@keyframes mdl-cel-chega {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
}
</style>
